import { assign_requestor_application_users, create_requestor_application_note, fetch_requestor_application_forms } from '@/services/requestor_portal_service';
import { create_application_status_approval_log, create_application_transition_log, fetch_assign_form_status_data, fetch_status_reviewers } from '@/services/workflow_service';
import { REASONING_TRAIL_PREFIX, application_form_id, error_message, normalize_assigned_form_status, normalize_reassign_user, normalize_text, reassign_status_filter, response_log_id, response_status_label, reviewer_payloads, reviewer_roles_from_payloads, reviewer_users_from_payloads, status_matches_reassign_filter, to_array, today_iso_date, unique_reassign_users, users_for_reviewer_roles, workflow_status_label } from './view_case_details_helpers';

function first_value(values = []) {
  return values.find(Boolean);
}
function missing_decision_status_message(instance) {
  return instance.decision_is_final_status
    ? 'This case is already in a final workflow status.'
    : 'Select a valid next status before confirming.';
}
function has_decision_status(instance) {
  return Boolean(instance.selected_decision_status);
}
function has_decision_context(instance) {
  return Boolean(instance.current_workflow_status?.id && instance.decision_application_form_id);
}
function decision_validation_error(instance) {
  if (!has_decision_status(instance)) {
    return missing_decision_status_message(instance);
  }

  if (!has_decision_context(instance)) {
    return 'This case is missing the workflow status context needed to confirm an outcome.';
  }

  return '';
}
function current_user_id(instance) {
  return Number(first_value([
    instance.$store?.getters?.currentUserId,
    sessionStorage.getItem('current_user_id'),
    sessionStorage.getItem('user_id'),
  ])) || null;
}
function decision_approval_payload(instance, selected_status_label) {
  return {
    application_form_id: instance.decision_application_form_id,
    form_status_id: Number(instance.current_workflow_status.id),
    action_taken: `Moved to ${selected_status_label}`,
    is_system_triggered: false,
  };
}
function decision_transition_payload(instance, approval_log_id, user_id, selected_status_label) {
  return {
    application_form_id: instance.decision_application_form_id,
    approval_log_id,
    from_status_id: Number(instance.current_workflow_status.id),
    to_status_id: Number(instance.selected_decision_status.id),
    note: instance.decision_reason || `Moved to ${selected_status_label}`,
    reviewer_id: user_id,
    is_system_triggered: false,
    entity_profile_id: instance.selected_entity_profile_id || null,
  };
}
function reasoning_note_request(instance, user_id, body) {
  return {
    application_id: instance.selected_application_id,
    user_id,
    journal_description: `${REASONING_TRAIL_PREFIX} ${body}`,
    hours_spent: '0.00',
    journal_date: today_iso_date(),
  };
}
async function create_reasoning_trail_entry(instance, user_id, body) {
  const normalized_body = normalize_text(body);
  if (!normalized_body || !instance.selected_application_id || !user_id) {
    return;
  }

  await create_requestor_application_note(reasoning_note_request(instance, user_id, normalized_body));
}
function decision_reasoning_body(selected_status_label, decision_reason) {
  const normalized_reason = normalize_text(decision_reason);
  if (!normalized_reason) {
    return '';
  }

  return `Decision outcome: ${selected_status_label}. ${normalized_reason}`;
}
async function submit_decision_update(instance) {
  const user_id = current_user_id(instance);
  const selected_status_label = workflow_status_label(instance.selected_decision_status);
  const approval_response = await create_application_status_approval_log(
    decision_approval_payload(instance, selected_status_label)
  );
  const approval_log_id = response_log_id(approval_response);

  if (!approval_log_id) {
    instance.decision_error = 'Status approval was submitted, but the backend did not return an approval log id.';
    return false;
  }
  const transition_response = await create_application_transition_log(
    decision_transition_payload(instance, approval_log_id, user_id, selected_status_label)
  );
  const confirmed_status = response_status_label(transition_response) || selected_status_label;
  instance.apply_case_status(confirmed_status);
  return {
    user_id,
    selected_status_label,
  };
}
async function refresh_confirmed_status(instance) {
  try {
    await instance.load_current_case_status({ throw_on_error: true });
    return true;
  } catch (refresh_error) {
    instance.decision_error = error_message(
      refresh_error,
      'Status was updated, but the confirmed backend status could not be refreshed.'
    );
    return false;
  }
}
function close_successful_decision(instance) {
  instance.show_decision_modal = false;
}
function handle_decision_error(instance, error) {
  instance.decision_error = error_message(error, 'Failed to update case status.');
}
async function confirm_decision_update(instance) {
  const updated = await submit_decision_update(instance);
  if (!updated) {
    return;
  }
  const refreshed = await refresh_confirmed_status(instance);
  if (!refreshed) {
    return;
  }
  await create_reasoning_trail_entry(
    instance,
    updated.user_id,
    decision_reasoning_body(updated.selected_status_label, instance.decision_reason)
  );
  await instance.load_notes();
  close_successful_decision(instance);
}
function reassign_validation_error(instance) {
  if (!instance.selected_application_id) {
    return 'No case reference was found for reassignment.';
  }

  if (!instance.selected_reassign_users.length) {
    return 'Select at least one user before reassigning.';
  }

  if (instance.selected_reassign_assignments.length !== instance.selected_reassign_users.length) {
    return 'One or more selected users is missing a role.';
  }

  return '';
}
function reassign_payload(instance) {
  return {
    application_id: instance.selected_application_id,
    assignments: instance.selected_reassign_assignments,
    note: instance.reassign_note,
  };
}

function time_label(value = new Date()) {
  return value.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function date_label(value = new Date()) {
  return value.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
  });
}

function selected_reassign_names(instance) {
  const selected_keys = new Set(to_array(instance.selected_reassign_users));
  return to_array(instance.reassign_panel_members)
    .filter((member) => selected_keys.has(member.key))
    .map((member) => member.name)
    .filter(Boolean);
}

function reasoning_reassign_item(instance) {
  const note = String(instance.reassign_note || '').trim();
  if (!note) {
    return null;
  }

  const now = new Date();
  const assignee_names = selected_reassign_names(instance);
  const assignee_summary = assignee_names.length
    ? `Reassigned to ${assignee_names.join(', ')}. `
    : '';

  return {
    id: `reassign-note-${now.getTime()}`,
    date: date_label(now),
    time: time_label(now),
    type: 'Human',
    tag_class: 'case-details-tag-human',
    body: `${assignee_summary}${note}`,
  };
}

function add_reassign_reasoning_item(instance) {
  const item = reasoning_reassign_item(instance);
  if (!item) {
    return;
  }

  instance.reasoning_items = [item, ...to_array(instance.reasoning_items)];
}
function reassign_reasoning_body(instance) {
  const note = normalize_text(instance.reassign_note);
  if (!note) {
    return '';
  }

  const assignee_names = selected_reassign_names(instance);
  return assignee_names.length
    ? `Reassigned to ${assignee_names.join(', ')}. ${note}`
    : note;
}

async function submit_reassign_update(instance) {
  await assign_requestor_application_users(reassign_payload(instance));
}
async function handle_reassign_success(instance) {
  instance.reassign_success = 'Case reassigned successfully.';
  const user_id = current_user_id(instance);
  const reasoning_body = reassign_reasoning_body(instance);

  if (instance.selected_application_id && user_id && reasoning_body) {
    await create_reasoning_trail_entry(instance, user_id, reasoning_body);
    await instance.load_notes();
  } else {
    add_reassign_reasoning_item(instance);
  }
  await instance.load_assigned_users();
  await instance.load_audit_logs();
  instance.show_reassign_modal = false;
}
function handle_reassign_error(instance, error) {
  instance.reassign_error = error_message(error, 'Failed to reassign case.');
}
async function reviewer_contexts_for_form(form_id, status_filter) {
  const response = await fetch_assign_form_status_data({ form_id });
  const assigned_statuses = first_value([
    response?.assigned_statuses,
    response?.data?.assigned_statuses,
    response?.selected_statuses,
    response?.data?.selected_statuses,
  ]) || [];
  return to_array(assigned_statuses)
    .map(normalize_assigned_form_status)
    .filter((status) => status.id && status_matches_reassign_filter(status, status_filter))
    .map((status) => ({
      form_id,
      status_id: status.id,
      workflow_status_id: status.status_id,
      status_name: status.name,
    }));
}

export default {
    close_decision_modal() {
      if (this.submitting_decision) {
        return;
      }

      this.show_decision_modal = false;
    },
    async confirm_decision() {
      this.decision_error = '';
      const validation_error = decision_validation_error(this);

      if (validation_error) {
        this.decision_error = validation_error;
        return;
      }

      this.submitting_decision = true;
      try {
        await confirm_decision_update(this);
      } catch (error) {
        handle_decision_error(this, error);
      } finally {
        this.submitting_decision = false;
      }
    },
    async reassign_reviewer_contexts(application_forms = []) {
      const status_filter = reassign_status_filter(this.$route?.query || {});
      const form_ids = [...new Set(
        application_forms
          .map(application_form_id)
          .filter(Boolean)
      )];

      const status_responses = await Promise.all(
        form_ids.map((form_id) => reviewer_contexts_for_form(form_id, status_filter))
      );

      return status_responses.flat();
    },
    async reviewer_users_for_contexts(contexts = []) {
      const reviewer_responses = await Promise.all(
        contexts.map((context) =>
          fetch_status_reviewers({
            form_id: context.form_id,
            status_id: context.status_id,
          })
        )
      );
      const reviewer_payload_list = reviewer_responses.flatMap(reviewer_payloads);
      const direct_reviewer_users = reviewer_users_from_payloads(reviewer_payload_list);
      const role_reviewer_users = await users_for_reviewer_roles(
        reviewer_roles_from_payloads(reviewer_payload_list)
      );

      return unique_reassign_users([
        ...direct_reviewer_users,
        ...role_reviewer_users,
      ]);
    },
    async load_reassign_users() {
      this.loading_reassign_users = true;
      this.reassign_error = '';
      this.reassign_panel_members = [];

      try {
        if (!this.selected_application_id) {
          this.reassign_error = 'No case reference was found for reassignment.';
          return;
        }

        const application_forms = await fetch_requestor_application_forms(this.selected_application_id);
        const reviewer_contexts = await this.reassign_reviewer_contexts(application_forms);
        const users = await this.reviewer_users_for_contexts(reviewer_contexts);
        this.reassign_panel_members = users
          .map(normalize_reassign_user)
          .filter((member) => member.user_id);
      } catch (error) {
        this.reassign_error = error_message(error, 'Failed to load workflow reviewers for reassignment.');
        this.reassign_panel_members = [];
      } finally {
        this.loading_reassign_users = false;
      }
    },
    async open_reassign_modal() {
      this.show_reassign_modal = true;
      this.reassign_success = '';
      this.reassign_error = '';
      this.selected_reassign_users = [];
      this.reassign_note = '';
      await this.load_reassign_users();
    },
    close_reassign_modal() {
      this.show_reassign_modal = false;
      this.reassign_note = '';
    },
    toggle_reassign_user(member = {}) {
      if (!member?.key) {
        return;
      }

      if (this.selected_reassign_users.includes(member.key)) {
        this.selected_reassign_users = this.selected_reassign_users.filter((key) => key !== member.key);
        return;
      }

      this.selected_reassign_users = [...this.selected_reassign_users, member.key];
    },
    confirm_reassign() {
      return confirm_valid_reassign(this);
    },
    set_tab(tab_key) {
      this.active_tab = tab_key;
    }
};

async function confirm_valid_reassign(instance) {
  instance.reassign_error = '';
  instance.reassign_success = '';
  const validation_error = reassign_validation_error(instance);

  if (validation_error) {
    instance.reassign_error = validation_error;
    return;
  }

  await confirm_reassign_update(instance);
}

async function confirm_reassign_update(instance) {
  instance.submitting_reassign = true;

  try {
    await submit_reassign_update(instance);
    await handle_reassign_success(instance);
  } catch (error) {
    handle_reassign_error(instance, error);
  } finally {
    instance.submitting_reassign = false;
  }
}
